import { Complaint, ComplaintStatus } from '../../core/models/complaint.model';

export type ColumnFilterType = 'select' | 'text';

export interface ColumnDef {
  key: string;
  label: string;
  /** Optional — off by default, added via the "+ Columns" picker on the list page. */
  extra: boolean;
  get: (item: Complaint) => string;
  /** Column width (table-layout: fixed) — keeps cells truncating cleanly instead of stretching. */
  width: string;
  /**
   * When set on an extra column, a matching control appears in the list filter bar
   * only while that column is enabled in "+ Columns".
   */
  filter?: ColumnFilterType;
}

export function meta(item: Complaint, key: string): string {
  const m = item.metadataJson;
  const v = m && typeof m === 'object' ? (m as Record<string, unknown>)[key] : null;
  return v == null || v === '' ? '—' : String(v);
}

export function fmtDate(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function assignedLabel(item: {
  assignedRole?: string | number;
  assignedOfficerRef?: string | number | null;
  metadataJson?: unknown;
}): string {
  const m =
    item.metadataJson && typeof item.metadataJson === 'object'
      ? (item.metadataJson as Record<string, unknown>)
      : {};
  const role = String(m['assignedRole'] || item.assignedRole || '').trim();
  const name = String(m['assignedOfficerName'] || '').trim();
  const ref = item.assignedOfficerRef;
  if (role && name) return `${role} · ${name}`;
  if (role) return String(role);
  if (name) return name;
  return ref != null ? String(ref) : '—';
}

/** Every field an officer might search by — S.No./Complaint ID lead the row; Date/Status trail it. */
export const COLUMNS: ColumnDef[] = [
  { key: 'subjectEnglish', label: 'Subject (English)', extra: false, width: '15rem', get: (c) => c.subject || '—' },
  { key: 'caseType', label: 'Case Type', extra: false, width: '9rem', get: (c) => c.category || '—' },
  {
    key: 'applicantName',
    label: 'Applicant Name',
    extra: false,
    width: '8.5rem',
    get: (c) => c.complainantName || c.citizenName || '—',
  },
  { key: 'village', label: 'Village / Address', extra: false, width: '9.5rem', get: (c) => meta(c, 'village') },
  { key: 'mobile', label: 'Mobile No.', extra: false, width: '7rem', get: (c) => meta(c, 'complainantMobile') },
  { key: 'assignedTo', label: 'Assigned to', extra: false, width: '7.5rem', get: (c) => assignedLabel(c) },
  {
    key: 'department',
    label: 'Department',
    extra: true,
    width: '8rem',
    filter: 'select',
    get: (c) => c.department || '—',
  },
  {
    key: 'subjectHindi',
    label: 'Subject (Hindi)',
    extra: true,
    width: '13rem',
    filter: 'text',
    get: (c) => meta(c, 'subjectHindi'),
  },
  {
    key: 'relativeName',
    label: "Father's / Husband's name",
    extra: true,
    width: '8.5rem',
    filter: 'text',
    get: (c) => meta(c, 'relativeName'),
  },
  { key: 'tehsil', label: 'Tehsil', extra: true, width: '6rem', filter: 'select', get: (c) => meta(c, 'tehsil') },
  {
    key: 'vikasKhand',
    label: 'Vikas Khand',
    extra: true,
    width: '6rem',
    filter: 'select',
    get: (c) => meta(c, 'vikasKhand'),
  },
  { key: 'thana', label: 'Thana', extra: true, width: '6rem', filter: 'select', get: (c) => meta(c, 'thana') },
  {
    key: 'gramPanchayat',
    label: 'Gram Panchayat',
    extra: true,
    width: '7rem',
    filter: 'select',
    get: (c) => meta(c, 'gramPanchayat'),
  },
  {
    key: 'category',
    label: 'Category (श्रेणी)',
    extra: true,
    width: '7.5rem',
    filter: 'select',
    get: (c) => meta(c, 'category'),
  },
  {
    key: 'forwardedTo',
    label: 'Forwarded to',
    extra: true,
    width: '7rem',
    filter: 'text',
    get: (c) => meta(c, 'forwardedTo'),
  },
  {
    key: 'araziKhasraNo',
    label: 'Arazi / Khasra No.',
    extra: true,
    width: '8rem',
    filter: 'text',
    get: (c) => meta(c, 'araziKhasraNo'),
  },
  { key: 'summaryHindi', label: 'Summary (Hindi)', extra: true, width: '15rem', get: (c) => meta(c, 'summaryHindi') },
  {
    key: 'summaryEnglish',
    label: 'Summary (English)',
    extra: true,
    width: '15rem',
    get: (c) => c.description || '—',
  },
  {
    key: 'rawOcrText',
    label: 'Original scan (OCR text)',
    extra: true,
    width: '15rem',
    get: (c) => meta(c, 'rawOcrText'),
  },
];

/** Groups the ~13 workflow statuses into a handful of visual tones for the status pill. */
const STATUS_TONE: Record<string, 'info' | 'progress' | 'good' | 'bad' | 'attn'> = {
  submitted: 'info',
  pending_verification: 'info',
  under_review: 'info',
  routed: 'progress',
  investigation_completed: 'progress',
  awaiting_hearing: 'progress',
  scheduled: 'progress',
  heard: 'progress',
  resolved: 'good',
  closed: 'good',
  rejected: 'bad',
  reopened: 'attn',
};

export function statusTone(status: string): string {
  return STATUS_TONE[status] || 'info';
}

/** Concatenates every field a case could be found by — same list the table's columns draw from,
 *  so anything searchable in the table is searchable here too, and vice versa. */
export function buildSearchText(item: Complaint): string {
  const parts = [
    String(item.trackingId ?? ''),
    String(item.id ?? ''),
    item.status,
    fmtDate(item.submittedAt),
    item.submittedAt || '',
    ...COLUMNS.map((c) => c.get(item)),
  ];
  return parts.join(' ').toLowerCase();
}

export type { Complaint, ComplaintStatus };
